import { ChangeEvent, useRef, useEffect } from 'react';
import { SelectChangeEvent, Theme, useMediaQuery } from '@mui/material';
import { CoreBox, CoreMenuItem, CoreTypography } from '@youscience/core';

import { DESKTOP_PER_PAGE_OPTIONS, MOBILE_PER_PAGE_OPTIONS } from '@constants';

import { sxStyles, StyledPaginationWrap, StyledSelectPerPage, StyledPagination } from './Pagination.styles';

interface PaginationProps {
  page: number;
  limit: number;
  totalCount?: number;
  selectPerPageOptions?: number[];
  isLoading: boolean;
  onPageSizeChange: (event: SelectChangeEvent<unknown>) => void;
  onPaginationChange: (event: ChangeEvent<unknown>, page: number) => void;
}

export const Pagination = (props: PaginationProps) => {
  const { onPageSizeChange, onPaginationChange, page, limit, totalCount = 0, selectPerPageOptions, isLoading } = props;
  const isMobileScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const isMobilePagination = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const lastValidPagesCount = useRef(Math.ceil(totalCount / limit));

  const paginationSize = isMobilePagination ? 'small' : 'medium';
  const selectPerPageItems =
    selectPerPageOptions || isMobileScreen ? MOBILE_PER_PAGE_OPTIONS : DESKTOP_PER_PAGE_OPTIONS;
  const pageInterval = `1-${limit} of ${totalCount}`;

  useEffect(() => {
    if (!isLoading) {
      lastValidPagesCount.current = Math.ceil(totalCount / limit);
    }
  }, [totalCount, limit, isLoading]);

  const pagesCount = isLoading ? lastValidPagesCount.current : Math.ceil(totalCount / limit);

  return (
    <StyledPaginationWrap data-testid="pagination">
      <CoreBox sx={sxStyles.selectShowOnPage}>
        <CoreTypography variant="caption">Show on page:</CoreTypography>

        <CoreBox sx={sxStyles.selectWrap}>
          <StyledSelectPerPage value={limit} onChange={onPageSizeChange}>
            {selectPerPageItems.map((item) => (
              <CoreMenuItem key={item} value={item.toString()}>
                {item}
              </CoreMenuItem>
            ))}
          </StyledSelectPerPage>
          {isMobileScreen && (
            <CoreTypography sx={sxStyles.pageInterval} variant="caption">
              {pageInterval}
            </CoreTypography>
          )}
        </CoreBox>
      </CoreBox>

      <StyledPagination
        page={page}
        size={paginationSize}
        count={pagesCount}
        onChange={onPaginationChange}
        disabled={isLoading}
      />
    </StyledPaginationWrap>
  );
};
